import { useState, useEffect } from "react";

const CACHE_KEY = "beme_gbp_krw_rate";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CacheEntry {
  rate: number;
  timestamp: number;
}

function readCache(): number | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
    return entry.rate;
  } catch {
    return null;
  }
}

function writeCache(rate: number): void {
  try {
    const entry: CacheEntry = { rate, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

/**
 * Fetches the live GBP → KRW exchange rate (cached for 30 min in localStorage).
 * Returns the rate (e.g. 1748.5 means £1 ≈ ₩1,748).
 *
 * Usage:
 *   const { rate } = useExchangeRate();
 *   const krw = rate ? toKRWLabel(gbpAmount * rate) : null;
 */
export function useExchangeRate(): { rate: number | null; loading: boolean } {
  const [rate, setRate] = useState<number | null>(() => readCache());
  const [loading, setLoading] = useState<boolean>(rate === null);

  useEffect(() => {
    if (rate !== null) return; // already have a fresh cached value

    let cancelled = false;
    setLoading(true);

    fetch("/api/exchange-rate")
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        console.log("[exchange-rate] response:", data);
        const fetched: number = data?.rates?.KRW;
        if (fetched && isFinite(fetched)) {
          writeCache(fetched);
          setRate(fetched);
        } else {
          console.warn("[exchange-rate] KRW rate missing in response");
        }
      })
      .catch((err) => {
        console.error("[exchange-rate] fetch error:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rate]);

  return { rate, loading };
}

/**
 * Converts a KRW amount to a Korean "약 X만원" label.
 *
 * Examples:
 *   49 300  →  "약 4.9만원"
 *   100 300 →  "약 10만원"
 *   170 000 →  "약 17만원"
 */
export function toKRWLabel(krw: number): string {
  return `약 ${Math.ceil(krw / 10000)}만원`;
}
